import React, { useState } from 'react';
import { C } from './modApi';

const h2 = { fontSize: 15, fontWeight: 700, margin: '0 0 6px', color: C.text };
const p = { fontSize: 12.5, color: C.text, lineHeight: 1.65, margin: '0 0 9px' };
const muted = { fontSize: 12, color: C.muted, lineHeight: 1.6, margin: '0 0 9px' };
const card = {
  background: '#fff', border: '1px solid ' + C.border,
  borderRadius: 10, padding: '13px 15px', marginBottom: 10,
};
const th = {
  textAlign: 'left', fontSize: 10.5, color: C.muted, fontWeight: 700,
  padding: '5px 8px', textTransform: 'uppercase', letterSpacing: 0.3,
};
const td = {
  fontSize: 12, color: C.text, padding: '6px 8px', verticalAlign: 'top',
  borderTop: '1px solid ' + C.border, lineHeight: 1.5,
};
const code = {
  background: C.surface, borderRadius: 6, padding: '9px 11px',
  fontSize: 11, fontFamily: 'monospace', color: C.text,
  whiteSpace: 'pre-wrap', lineHeight: 1.6, margin: '0 0 9px',
};

function Section({ n, title, children, open, onToggle }) {
  return (
    <div style={card}>
      <div onClick={onToggle}
        style={{ display: 'flex', justifyContent: 'space-between',
                 alignItems: 'center', cursor: 'pointer', gap: 8 }}>
        <span style={{ ...h2, margin: 0 }}>
          <span style={{ color: C.muted, fontWeight: 400 }}>{n}. </span>{title}
        </span>
        <span style={{ fontSize: 13, color: C.muted }}>{open ? '\u2212' : '+'}</span>
      </div>
      {open ? <div style={{ marginTop: 11 }}>{children}</div> : null}
    </div>
  );
}

export default function HowItWorks({ me }) {
  const [open, setOpen] = useState(1);
  const t = function (n) { return { open: open === n, onToggle: function () { setOpen(open === n ? 0 : n); } }; };

  return (
    <div>
      <div style={{ background: C.primaryBg, border: '1px solid ' + C.primary,
                    borderRadius: 10, padding: '12px 15px', marginBottom: 12 }}>
        <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: C.primary }}>
          A shared workspace for the Pi moderation team
        </p>
        <p style={{ margin: 0, fontSize: 12, color: C.text, lineHeight: 1.6 }}>
          This is not a chat app. Discord and Telegram stay exactly as they are.
          This is the layer underneath — the team list, the roll calls,
          the shared answers, and the record.
        </p>
      </div>

      {/* ---------------- 1 ---------------- */}
      <Section n={1} title="Why this exists" {...t(1)}>
        <p style={p}>
          The moderation team runs on scattered chats. That creates four problems, every day.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 9 }}>
          <thead><tr><th style={th}>Problem</th><th style={th}>What it costs</th></tr></thead>
          <tbody>
            <tr>
              <td style={{ ...td, fontWeight: 600 }}>Nobody knows who is on duty</td>
              <td style={td}>A question sits unanswered for hours because the mods who were online had already logged off, and nobody knew.</td>
            </tr>
            <tr>
              <td style={{ ...td, fontWeight: 600 }}>The same questions get answered from scratch</td>
              <td style={td}>Twenty mods each type their own version of the same answer. Some are outdated. Some are wrong.</td>
            </tr>
            <tr>
              <td style={{ ...td, fontWeight: 600 }}>Language is a wall</td>
              <td style={td}>A mod in Nigeria gets a question in Hausa. The good answer exists — in English, in someone else&apos;s head.</td>
            </tr>
            <tr>
              <td style={{ ...td, fontWeight: 600 }}>No record of anything</td>
              <td style={td}>Who added this mod? Are they still active? Did they ever reply to anything? Nobody can say.</td>
            </tr>
          </tbody>
        </table>
        <p style={muted}>This portal fixes those four things. Nothing more, nothing less.</p>
      </Section>

      {/* ---------------- 2 ---------------- */}
      <Section n={2} title="Who can do what" {...t(2)}>
        <p style={muted}>Three levels. Each one can do everything the level below it can.</p>

        <p style={{ ...h2, fontSize: 13 }}>Moderator</p>
        <p style={p}>
          The people doing the daily work in channels. They can see the whole team,
          search the shared Q&amp;A and copy answers, add new Q&amp;A entries and
          translations, reply to roll calls, receive and complete tasks, write their
          own introduction, vote on other introductions, and edit their own profile.
        </p>

        <p style={{ ...h2, fontSize: 13 }}>Mega Mod</p>
        <p style={p}>
          Team leads. Everything a Mod can do, plus: <b>add new Moderators</b>,
          deactivate or reactivate them, create roll calls and see the full report,
          send reminders, assign tasks, post notices and scam alerts, and see any
          moderator&apos;s full record.
        </p>
        <p style={muted}>
          A Mega Mod cannot change another Mega Mod. That is deliberate — see section 9.
        </p>

        <p style={{ ...h2, fontSize: 13 }}>Portal Admin</p>
        <p style={p}>
          Currently <b>@MrByte</b>. Everything a Mega Mod can do, plus: add Mega Mods,
          promote or demote, change any moderator&apos;s status including a Mega Mod&apos;s,
          and broadcast to the whole team.
        </p>
      </Section>

      {/* ---------------- 3 ---------------- */}
      <Section n={3} title="Getting in" {...t(3)}>
        <p style={{ ...h2, fontSize: 13 }}>There is no separate account</p>
        <p style={p}>
          The portal uses the PenFlow account, which is the person&apos;s <b>real Pi
          account</b>. Nobody creates a password. Nobody manages a user list.
        </p>
        <div style={code}>{
          '1.  The moderator opens PenFlow in the Pi Browser\n' +
          '    and signs in with Pi. That is it - their account exists.\n\n' +
          '2.  A Mega Mod adds them:\n' +
          '    Team tab -> + Add moderator -> their Pi username\n\n' +
          '3.  They get a notification - bell, Discord DM, Telegram DM -\n' +
          '    linking straight to the portal.\n\n' +
          '4.  They open the portal and they are in.'
        }</div>
        <p style={{ ...h2, fontSize: 13 }}>Why this way</p>
        <p style={p}>
          If someone is not a real Pi user, they cannot get in. There is no invite link
          to leak, no password to share, no separate identity to impersonate.
          The Pi account <i>is</i> the credential.
        </p>
      </Section>

      {/* ---------------- 4 ---------------- */}
      <Section n={4} title="The Team tab" {...t(4)}>
        <p style={p}>
          The first screen. It answers: <b>who is on this team, where are they,
          and are they still around?</b>
        </p>

        <p style={{ ...h2, fontSize: 13 }}>Filters</p>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 11 }}>
          <thead><tr><th style={th}>Button</th><th style={th}>What it does</th><th style={th}>Why</th></tr></thead>
          <tbody>
            <tr>
              <td style={{ ...td, fontWeight: 600 }}>By country</td>
              <td style={td}>Groups the list by country with a flag, and the cities inside each one</td>
              <td style={td}>Shows the gaps. If a country has hundreds of thousands of Pioneers and one mod, that is now visible instead of assumed.</td>
            </tr>
            <tr>
              <td style={{ ...td, fontWeight: 600 }}>Active / Inactive / Removed</td>
              <td style={td}>Filters by status</td>
              <td style={td}>Inactive is not the same as removed. Someone on exam break should not be treated as gone.</td>
            </tr>
            <tr>
              <td style={{ ...td, fontWeight: 600 }}>All countries</td>
              <td style={td}>Jumps to one country</td>
              <td style={td}>Useful when a regional issue comes up and you need that region&apos;s people.</td>
            </tr>
            <tr>
              <td style={{ ...td, fontWeight: 600 }}>+ Add moderator</td>
              <td style={td}>Opens the add form</td>
              <td style={td}>Mega Mods and above only.</td>
            </tr>
          </tbody>
        </table>

        <p style={{ ...h2, fontSize: 13 }}>A moderator card</p>
        <div style={code}>{
          '\u25CF  Ali Raza   MOD                    seen 2h ago\n' +
          '@aliraza - Lagos, Nigeria - 7pm-12am (WAT)\n' +
          'Channels: #pi-help, #newbies\n' +
          'Speaks: English, Hausa\n' +
          'added by Sarah\n' +
          '3x active - 1x inactive\n\n' +
          '[Introduction]  [Record]  [Deactivate]'
        }</div>
        <p style={muted}>Every part of that is there for a reason:</p>
        <ul style={{ ...muted, paddingLeft: 17, margin: '0 0 9px' }}>
          <li><b>The dot</b> — green active, amber inactive, grey removed. Readable in half a second.</li>
          <li><b>The flag</b> — an image, not an emoji, because emoji flags do not render on many Android phones.</li>
          <li><b>&quot;seen 2h ago&quot;</b> — the real signal of whether someone is still with the team. Measured, not claimed.</li>
          <li><b>Hours + timezone</b> — so nobody expects a reply from Lagos at 4am WAT.</li>
          <li><b>Channels</b> — so a question in #newbies goes to someone who actually watches #newbies.</li>
          <li><b>Languages</b> — so a Hausa question reaches a Hausa speaker.</li>
          <li><b>&quot;added by&quot;</b> — accountability. Every moderator traces back to someone.</li>
          <li><b>&quot;3x active&quot;</b> — only shows when there has been churn. Someone who has left and returned three times is a different situation from a steady one.</li>
        </ul>

        <p style={{ ...h2, fontSize: 13 }}>Adding a moderator</p>
        <p style={p}>
          Only the <b>Pi username</b> is required. Everything else — country, city,
          timezone, channels, hours, languages — is optional, because
          <b> the moderator fills that in themselves</b>. A Mega Mod adding twenty
          people should not have to interview each one first.
        </p>
        <p style={muted}>
          If the username is not found, the portal says so directly: the person needs
          to open PenFlow and sign in with Pi once, and then they can be added.
        </p>
      </Section>

      {/* ---------------- 5 ---------------- */}
      <Section n={5} title="Introductions and voting" {...t(5)}>
        <p style={p}>
          <b>The problem:</b> a Mega Mod adds someone. Nobody else knows who that
          person is. Six months later there are forty mods and no shared sense of
          who is who.
        </p>
        <p style={p}>
          <b>The moderator writes their own introduction</b> — who they are, how long
          they have been in Pi, which channels they help in, what they are good at.
          Only they can write or edit it.
        </p>
        <p style={p}>
          <b>Other moderators vote on it.</b> Two options, both with an optional comment:
          <b> Support</b> (I know this person, they are solid) or
          <b> Concern</b> (I have a reason to be careful here). One vote per person,
          changeable later. Nobody can vote on their own.
        </p>
        <p style={{ ...h2, fontSize: 13 }}>Why &quot;Concern&quot; and not just an upvote</p>
        <p style={p}>
          An up-only system produces a wall of green ticks that means nothing. The value
          is in the disagreement. If someone has eleven Supports and one Concern with a
          comment explaining it, that one comment is the most useful thing on the page —
          and a Mega Mod can look into it before handing that person more responsibility.
        </p>
      </Section>

      {/* ---------------- 6 ---------------- */}
      <Section n={6} title="The Record" {...t(6)}>
        <p style={muted}>Mega Mods and above.</p>
        <p style={p}>
          The honest answer to <i>&quot;is this person actually contributing?&quot;</i>
        </p>
        <div style={code}>{
          '  3          1            14          22           9\n' +
          'ACTIVATED  DEACTIVATED  ROLL CALLS  TASKS DONE  Q&A ADDED\n\n' +
          'Status changes\n' +
          'active -> inactive - by Sarah - 12-Jun-26\n' +
          '  "Exams until July, will be back"\n' +
          'inactive -> active - by Sarah - 18-Jul-26\n' +
          'new -> active - by MrByte - 03-Feb-26'
        }</div>
        <p style={muted}>Five numbers and a timeline. No opinions.</p>
        <ul style={{ ...muted, paddingLeft: 17, margin: '0 0 9px' }}>
          <li><b>Activated / Deactivated</b> — churn. Someone at 5× / 4× is unreliable in a way no single conversation would reveal.</li>
          <li><b>Roll calls answered</b> — do they respond when the team asks something?</li>
          <li><b>Tasks done</b> — do they finish what they take on?</li>
          <li><b>Q&amp;A added</b> — are they building the shared knowledge, or only drawing from it?</li>
        </ul>
        <p style={p}>
          The timeline records every change: what it went from, what it went to, who did
          it, and the reason they typed. <b>Nothing here can be edited or deleted</b> —
          which protects both the team and the person.
        </p>
      </Section>

      {/* ---------------- 7 ---------------- */}
      <Section n={7} title="Roll calls" {...t(7)}>
        <p style={p}>
          <b>The main tool.</b> This is how the team gets a straight answer from
          everyone at once.
        </p>
        <p style={{ ...h2, fontSize: 13 }}>The design decision that matters</p>
        <p style={p}>
          <b>The questions are not fixed.</b> Most attendance systems ship a form and you
          live with it forever. Here, the Mega Mod writes their own questions every
          single time. A roll call in February might ask <i>&quot;which channel do you
          moderate?&quot;</i> — one in August might ask <i>&quot;have you seen the new scam
          pattern in your region?&quot;</i> Same tool, completely different purpose.
        </p>

        <p style={{ ...h2, fontSize: 13 }}>Seven question types</p>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 11 }}>
          <thead><tr><th style={th}>Type</th><th style={th}>Use it for</th></tr></thead>
          <tbody>
            <tr><td style={{ ...td, fontWeight: 600 }}>Short text</td><td style={td}>&quot;Which channels do you moderate?&quot;</td></tr>
            <tr><td style={{ ...td, fontWeight: 600 }}>Long text</td><td style={td}>&quot;What is the biggest problem you are seeing right now?&quot;</td></tr>
            <tr><td style={{ ...td, fontWeight: 600 }}>Yes / No</td><td style={td}>&quot;Are you still active?&quot;</td></tr>
            <tr><td style={{ ...td, fontWeight: 600 }}>Pick one</td><td style={td}>&quot;Hours per week — under 5 / 5-15 / 15+&quot;</td></tr>
            <tr><td style={{ ...td, fontWeight: 600 }}>Pick several</td><td style={td}>&quot;Which languages can you answer in?&quot;</td></tr>
            <tr><td style={{ ...td, fontWeight: 600 }}>Time / hours</td><td style={td}>&quot;What hours are you usually available?&quot;</td></tr>
            <tr><td style={{ ...td, fontWeight: 600 }}>Which Mega Mod</td><td style={td}>&quot;Who added you?&quot; — the list fills in automatically</td></tr>
          </tbody>
        </table>
        <p style={muted}>
          Each question can be required or optional, and can carry a hint line.
          A roll call can be aimed at everyone, only Mods, or only Mega Mods.
        </p>

        <p style={{ ...h2, fontSize: 13 }}>Sending and replying</p>
        <p style={p}>
          A roll call starts as a <b>draft</b> — nobody sees it. The Mega Mod checks it
          over, then presses <b>Open</b>. Every moderator it applies to is notified
          instantly: bell, Discord DM, Telegram DM, linking straight to the form.
        </p>
        <p style={p}>
          <b>One reply per person</b> — no second submission, no editing after the fact.
          That keeps the report honest. Required questions are enforced. The Mega Mod
          gets a notification on every reply, with a running count.
        </p>

        <p style={{ ...h2, fontSize: 13 }}>The report — the payoff</p>
        <div style={code}>{
          'August check-in              14 of 24 replied - 58%\n\n' +
          'Q2: Are you still active?\n' +
          '    Yes - 13     No - 1\n\n' +
          'Q3: Who added you?\n' +
          '    Sarah - 8    MrByte - 4    Ahmed - 2\n\n' +
          'Did not reply (10)\n' +
          '    Budi (mod)   - last seen 22d ago\n' +
          '    Linh (mod)   - last seen 3h ago\n' +
          '    Carlos (mod) - never'
        }</div>
        <p style={p}>
          Choice-type questions are counted automatically. Text questions come back as
          a readable list.
        </p>
        <p style={p}>
          <b>The &quot;did not reply&quot; list is the most valuable part of the screen.</b>{' '}
          It shows <i>why</i> each person is missing. Linh was online three hours ago and
          did not reply — that is a different conversation from Budi, who has not opened
          the portal in three weeks, and Carlos, who has never opened it at all.
        </p>
        <p style={muted}>
          <b>Remind</b> messages only the people who have not replied. Nobody who already
          answered gets pinged again.
        </p>
      </Section>

      {/* ---------------- 8 ---------------- */}
      <Section n={8} title="Tasks" {...t(8)}>
        <p style={p}>
          Small, direct, trackable work. A Mega Mod assigns a task to one moderator:
          title, detail, priority (low / normal / high / urgent), optional due date.
          The moderator is notified immediately.
        </p>
        <p style={p}>
          They then move it: <b>Start</b> → <b>Done</b>, and can write a reply explaining
          what they did. When it is marked done, the Mega Mod who assigned it is notified.
          Overdue tasks are flagged automatically.
        </p>
        <p style={muted}>
          <b>Why it is deliberately simple:</b> this is not a project management tool.
          It is for <i>&quot;check the reports in #pi-help from last night&quot;</i> — the kind
          of thing that gets lost in a chat scroll and then never happens.
        </p>
      </Section>

      {/* ---------------- 9 ---------------- */}
      <Section n={9} title="Q&A — the shared answer library" {...t(9)}>
        <p style={p}>
          <b>The feature the team will use most.</b> A moderator sees a question in a
          channel, opens the Q&amp;A, types two or three words, and the matching answer
          comes up. They tap <b>Copy</b>, paste it into the chat, done.
        </p>
        <p style={muted}>
          Search covers the question, the answer body, the keywords and the category —
          so a mod who half-remembers the topic still finds it.
        </p>

        <p style={{ ...h2, fontSize: 13 }}>Multi-language — the important part</p>
        <div style={code}>{
          '"How do I complete Mainnet migration?"\n' +
          'Category: KYC - used 247 times\n\n' +
          '  English      [Copy]   used 180x\n' +
          '  Urdu         [Copy]   used 31x\n' +
          '  Hausa        [Copy]   used 22x\n' +
          '  Vietnamese   [Copy]   used 14x\n\n' +
          '  + Add: Indonesian, Turkish, Bengali, Swahili...'
        }</div>
        <p style={p}>
          <b>One question. Many answers.</b> 18 languages are available, built around
          where Pioneers actually are — Hausa, Swahili, Vietnamese, Indonesian, Bengali
          and Persian are on the list for the same reason English is.
        </p>
        <p style={p}>
          The English version is the original; every other language is a translation
          attached to it. That structure matters: one question edited in one place so
          nothing drifts, adding a language means no re-writing, and a moderator can see
          at a glance <b>which languages are still missing</b> and fill the gap.
        </p>
        <p style={{ ...h2, fontSize: 13 }}>Usage counts</p>
        <p style={p}>
          Every copy is counted, and the most-used answers rise to the top automatically.
          This is not a vanity metric — it tells the team what Pioneers are actually
          confused about. An answer copied 247 times is pointing at something that needs
          fixing upstream, not just answering downstream.
        </p>
        <p style={muted}>
          Any moderator can add an entry or a translation. The author or any Mega Mod can
          edit. Only a Mega Mod can delete.
        </p>
      </Section>

      {/* ---------------- 10 ---------------- */}
      <Section n={10} title="Notices and scam alerts" {...t(10)}>
        <p style={p}>
          A short board for things the whole team needs to know. Three kinds:
          <b> Notice</b> (general information), <b>Update</b> (something has changed),
          and <b>Scam alert</b> (a new scam pattern spotted).
        </p>
        <p style={p}>
          <b>Scam alerts are different.</b> Posting one sends an immediate notification
          to every active moderator — bell, Discord, Telegram. Not a post they might
          scroll past later.
        </p>
        <p style={muted}>
          In Pi, a new scam pattern spreads across regions within hours. The delay
          between one mod noticing it and every mod knowing about it is measured in real
          losses to real Pioneers. That is what this exists to shorten.
        </p>
      </Section>

      {/* ---------------- 11 ---------------- */}
      <Section n={11} title="Broadcast" {...t(11)}>
        <p style={muted}>Portal admin only.</p>
        <p style={p}>
          A message to the whole team, or a slice of it: <b>everyone</b>,
          <b> only Mods</b>, <b>only Mega Mods</b>, or <b>one country</b>.
          Delivered as a bell notification plus a Discord and Telegram DM, sent in the
          background so a large team does not lock up the screen.
        </p>
        <p style={muted}>
          Restricted on purpose. If every Mega Mod could message the entire team,
          the team would stop reading them.
        </p>
      </Section>

      {/* ---------------- 12 ---------------- */}
      <Section n={12} title="Protections" {...t(12)}>
        <p style={muted}>
          Enforced in the backend, not just hidden in the interface. Calling the API
          directly hits the same walls.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Rule</th><th style={th}>Reason</th></tr></thead>
          <tbody>
            <tr><td style={td}>A Mod cannot add anyone, change a status, or promote themselves</td><td style={td}>The obvious one</td></tr>
            <tr><td style={td}><b>A Mega Mod cannot change another Mega Mod</b></td><td style={td}>Otherwise two who disagree can deactivate each other, and the team wakes up to empty leadership</td></tr>
            <tr><td style={td}>Nobody but a portal admin can touch a portal admin</td><td style={td}>The portal cannot be locked out, no matter how many Mega Mods exist</td></tr>
            <tr><td style={td}>Only a portal admin can promote or demote</td><td style={td}>Level changes are structural — they do not happen sideways</td></tr>
            <tr><td style={td}>Only a portal admin can broadcast</td><td style={td}>Protects the signal</td></tr>
            <tr><td style={td}>Nobody can vote on their own introduction</td><td style={td}>—</td></tr>
            <tr><td style={td}>One roll call reply per person, no edits</td><td style={td}>Keeps the report trustworthy</td></tr>
            <tr><td style={td}>Deactivating requires a reason</td><td style={td}>And it is permanent in the record</td></tr>
            <tr><td style={td}>Status history cannot be edited or deleted</td><td style={td}>By anyone, including portal admins</td></tr>
          </tbody>
        </table>
      </Section>

      {/* ---------------- 13 ---------------- */}
      <Section n={13} title="What reaches a moderator" {...t(13)}>
        <p style={muted}>
          Every one of these arrives three ways at once: PenFlow bell + Discord DM +
          Telegram DM, for whoever has linked those accounts. Each links to the exact
          screen, not the front page.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Event</th><th style={th}>Who gets it</th></tr></thead>
          <tbody>
            <tr><td style={td}>Added to the team</td><td style={td}>The new moderator</td></tr>
            <tr><td style={td}>Status or level changed</td><td style={td}>The moderator affected</td></tr>
            <tr><td style={td}>Roll call opened</td><td style={td}>Everyone it applies to</td></tr>
            <tr><td style={td}>Roll call reminder</td><td style={td}>Only those who have not replied</td></tr>
            <tr><td style={td}>Someone replied</td><td style={td}>The Mega Mod who created it</td></tr>
            <tr><td style={td}>New task assigned</td><td style={td}>The moderator it went to</td></tr>
            <tr><td style={td}>Task marked done</td><td style={td}>The Mega Mod who assigned it</td></tr>
            <tr><td style={td}>Vote on your introduction</td><td style={td}>The moderator</td></tr>
            <tr><td style={{ ...td, fontWeight: 700, color: C.danger }}>Scam alert posted</td><td style={{ ...td, fontWeight: 700 }}>Every active moderator</td></tr>
            <tr><td style={td}>Broadcast</td><td style={td}>Whoever matches the filter</td></tr>
          </tbody>
        </table>
      </Section>

      {/* ---------------- 14 ---------------- */}
      <Section n={14} title="A week in practice" {...t(14)}>
        <p style={p}>
          <b>Monday.</b> MrByte adds four new moderators from Indonesia and Vietnam.
          Each gets a DM within seconds and opens the portal. They fill in their own
          channels, hours and languages, and write their introductions.
        </p>
        <p style={p}>
          <b>Tuesday.</b> The existing team reads the four introductions. Support votes
          come in; on one, a moderator leaves a Concern with a note. Sarah reads it and
          follows up privately before assigning that person anything.
        </p>
        <p style={p}>
          <b>Wednesday.</b> Sarah creates a roll call — five questions of her own,
          including <i>&quot;which scam patterns are you seeing in your region?&quot;</i>
          She opens it. 24 people are notified.
        </p>
        <p style={p}>
          <b>Thursday.</b> 14 have replied. Sarah opens the report. Three of the ten
          missing have not opened the portal in over two weeks; she presses <b>Remind</b>,
          and the seven who were recently active get a nudge. The scam question has
          surfaced a pattern that four different regions reported independently.
        </p>
        <p style={p}>
          <b>Thursday, later.</b> Sarah posts a <b>scam alert</b>. Every active mod is
          notified within seconds. Ahmed writes the answer into the Q&amp;A in English;
          by evening it exists in Hausa, Vietnamese, Indonesian and Urdu, added by the
          mods who speak those languages.
        </p>
        <p style={p}>
          <b>Friday.</b> A Pioneer asks about that exact scam in #pi-help, in Vietnamese.
          Linh searches two words, taps Copy, and pastes a correct, team-approved answer
          in the Pioneer&apos;s own language. It took eleven seconds.
        </p>
        <p style={p}>
          <b>The following month.</b> Sarah opens Budi&apos;s Record. Two roll calls
          missed, no tasks completed, last seen 31 days ago. She deactivates with the
          reason <i>&quot;No response since July, will reactivate if they return.&quot;</i>
          It is on the record. Nothing is deleted, and nothing is guessed at.
        </p>
      </Section>

      {/* ---------------- 15 ---------------- */}
      <Section n={15} title="What this is not" {...t(15)}>
        <p style={muted}>
          Being clear about the edges matters as much as the features.
        </p>
        <ul style={{ ...p, paddingLeft: 17 }}>
          <li><b>Not a chat app.</b> Discord and Telegram do that well. This does not compete with them.</li>
          <li><b>Not a ticketing system.</b> Pioneer-facing support stays where it is.</li>
          <li><b>Not surveillance.</b> It records what people do inside the portal — replies, tasks, entries, status changes. It does not track anything outside it.</li>
          <li><b>Not automatic moderation.</b> No bans, no deletions, no actions on Pi channels. It is a coordination layer for the humans doing that work.</li>
        </ul>
      </Section>

      <p style={{ fontSize: 11, color: C.muted, textAlign: 'center', margin: '14px 0 0' }}>
        Sign in with your Pi account, then ask a Mega Mod to add you.
      </p>
    </div>
  );
}
